import { useAuth0 } from "@auth0/auth0-react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import OwnerReservationTable from "@/components/Owners/OwnerReservationTable.tsx";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import DataTable from "@/components/Table/DataTable.tsx";
import SearchSection from "@/components/Table/SearchSection.tsx";
import { useDeleteReservationMutation } from "@/mutations/reservation.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { ListReservation } from "@/queries/reservations.ts";
import type { ListUnit } from "@/queries/units.ts";

export const Route = createFileRoute("/owner/reservation/")({
    component: RouteComponent,
});

function RouteComponent() {
    const { user } = useAuth0();

    if (!user?.email) {
        throw new Error("Auth0 user with no email.");
    }

    const qof = useQueryOptionsFactory();
    const { data: reservationsData, refetch } = useSuspenseQuery(
        qof.reservations.listCurrentAndFuture(),
    );
    const reservations = reservationsData.data;
    const { data } = useSuspenseQuery(qof.units.list());
    const units = data.data;

    return <DisplayComponent reservations={reservations} refetch={refetch} units={units} />;
}

const instructions = [
    "To find a particular reservation for this rental, type the Last Name or Confirmation Number in the text box below and click on Search button.",
    "To order reservations by Date, Guest Last Name, Confirmation Number or Email just click on column titles below and they will be ordered.",
    "To see more information for each Guest and/or to Edit their information, click the Info/Edit button.",
    "The dates are the DAYS on which the guest checks in and checks out.",
];

type Props = {
    reservations: ListReservation[];
    refetch: () => void;
    units: ListUnit[];
};

type SortField = keyof ListReservation;
type SortDirection = "asc" | "desc";

const DisplayComponent = ({ reservations, refetch, units }: Props) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [reservationToDelete, setReservationToDelete] = useState<ListReservation | null>(null);
    const [sortField, setSortField] = useState<SortField>("startDate");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const deleteReservation = useDeleteReservationMutation();
    const navigate = useNavigate();

    const ownerName = `${units[0]?.owner?.ownerFirstName} ${units[0]?.owner?.ownerName}`;

    const displayReservations = useMemo(() => {
        return reservations
            .filter(
                (r) =>
                    !searchTerm ||
                    r.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    r.confirmationNumber.includes(searchTerm),
            )
            .sort((a, b) => {
                const invert = sortDirection === "asc" ? -1 : 1;
                if (typeof a[sortField] === "string" && typeof b[sortField] === "string") {
                    return invert * a[sortField].localeCompare(b[sortField]);
                }

                if (a[sortField] instanceof Date && b[sortField] instanceof Date) {
                    return invert * (a[sortField].getTime() - b[sortField].getTime());
                }
                return 0;
            });
    }, [reservations, searchTerm, sortField, sortDirection]);

    useEffect(() => {
        if (deleteReservation.data) {
            setReservationToDelete(null);
            refetch();
        }
    }, [deleteReservation.data]);

    const cancelDelete = () => {
        setReservationToDelete(null);
    };
    const handleDelete = (reservation: ListReservation) => {
        setReservationToDelete(reservation);
    };
    const handleEdit = (reservation: ListReservation) => {
        navigate({
            to: `/owner/reservation/${reservation.id}/edit`,
        }).catch((e) => console.error("failed to navigate", e));
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection(field === "startDate" || field === "endDate" ? "desc" : "asc");
        }
    };

    const confirmDelete = () => {
        console.info("calling delete");
        if (reservationToDelete) {
            deleteReservation.mutate(reservationToDelete);
        }
    };

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader title={`Aloha ${ownerName}`} instructions={instructions} />

                <SearchSection
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onClearSearch={() => setSearchTerm("")}
                    placeholder="Enter Last Name or Confirmation Number"
                    displayedCount={displayReservations.length}
                    totalCount={reservations.length}
                    totalAllCount={reservations.length}
                />

                <DataTable
                    emptyMessage={
                        searchTerm
                            ? "No reservations found matching your search."
                            : "No reservations!"
                    }
                    colSpan={8}
                    maxHeight={{
                        xs: "600px",
                        sm: "600px",
                    }}
                    loadingMessage="Loading more reservations..."
                    endMessage="No more reservations to load"
                    isLoading={false}
                    hasMore={false}
                    onLoadMore={() => {
                        /*no more this is all we got*/
                    }}
                    isEmpty={reservations.length === 0}
                >
                    <OwnerReservationTable
                        data={displayReservations}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </DataTable>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={reservationToDelete !== null}
                onClose={cancelDelete}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    root: {
                        sx: {
                            borderRadius: 3,
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: "#fef2f2",
                        color: "#dc2626",
                        fontWeight: 600,
                        borderBottom: "1px solid #fecaca",
                    }}
                >
                    Confirm Delete Reservation
                </DialogTitle>

                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Typography variant="body1" sx={{ color: "#374151", mb: 2 }}>
                        Are you sure you want to delete this reservation for{" "}
                        {reservationToDelete?.customerName}?
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        This action cannot be undone. The reservation will be permanently removed
                        from the system.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 3, justifyContent: "flex-end", gap: 2 }}>
                    <Button
                        onClick={cancelDelete}
                        sx={{
                            color: "#6b7280",
                            textTransform: "none",
                            px: 3,
                            py: 1,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: "#dc2626",
                            "&:hover": { backgroundColor: "#b91c1c" },
                            textTransform: "none",
                            px: 3,
                            py: 1,
                        }}
                    >
                        Delete Reservation
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessToast
                open={deleteReservation.data !== undefined}
                onClose={() => deleteReservation.reset()}
                title="Reservation Deleted Successfully!"
                message={
                    deleteReservation.data
                        ? `Reservation for ${deleteReservation.data?.customerName} (${deleteReservation.data?.confirmationNumber}) has been removed.`
                        : "The reservation has been removed."
                }
                backgroundColor="#ef4444"
            />
        </Box>
    );
};
